import { format } from 'date-fns'
import type { Blackout, PublicHoliday } from '@lms/shared-types'

/**
 * UpcomingPressureRow — inline preview of the next blackout or public
 * holiday on the runway, rendered as a list item.
 *
 * Adopted from the prototype Division-Capacity sidebar
 * (`prototype/director.jsx:191-205`) so Director can glance at coverage
 * pressure without opening the HOD overview page.
 *
 * Owned by `DirectorDashboardPage`. Kept here because the row is >40
 * lines, has its own helpers, and is reused across the Director-dashboard
 * tests and stories.
 */
export interface UpcomingPressureItem {
  /** Stable React key — `kind:identifier`. */
  key: string
  kind: 'blackout' | 'public-holiday'
  /** Display name (e.g. "Product Launch", "Deepavali"). */
  label: string
  /** ISO-date the event starts, used for sorting and the countdown. */
  startDate: string
  /** ISO-date the event ends (same as startDate for single-day holidays). */
  endDate: string
}

interface UpcomingPressureRowProps {
  item: UpcomingPressureItem
}

export function UpcomingPressureRow({ item }: UpcomingPressureRowProps): JSX.Element {
  const isBlackout = item.kind === 'blackout'
  const days = daysUntil(item.startDate)
  const countdown = countdownLabel(days)
  const range = formatDateRange(item.startDate, item.endDate)
  const tagBg = isBlackout ? 'var(--warning-bg)' : 'var(--info-bg)'
  const tagFg = isBlackout ? 'var(--warning-fg)' : 'var(--info-fg)'
  const tagLabel = isBlackout ? 'BLACKOUT' : 'HOLIDAY'
  return (
    <li
      data-testid="director-upcoming-row"
      style={{
        padding: '12px 0',
        borderBottom: '1px solid var(--french-gray-2)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              padding: '2px 8px',
              borderRadius: 999,
              background: tagBg,
              color: tagFg,
            }}
          >
            {tagLabel}
          </span>
          <strong style={{ fontSize: 14, color: 'var(--text)' }}>{item.label}</strong>
          <span style={{ fontSize: 13, color: 'var(--text-2)' }}>{range}</span>
        </div>
        <span
          data-testid="director-upcoming-countdown"
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: days <= 7 ? 'var(--warning-fg)' : 'var(--text-3)',
          }}
        >
          {countdown}
        </span>
      </div>
    </li>
  )
}

export function blackoutToPressure(b: Blackout): UpcomingPressureItem {
  return {
    key: `blackout:${b.id}`,
    kind: 'blackout',
    label: b.name,
    startDate: b.startDate,
    endDate: b.endDate,
  }
}

export function holidayToPressure(h: PublicHoliday): UpcomingPressureItem {
  return {
    key: `public-holiday:${h.date}:${h.region}`,
    kind: 'public-holiday',
    label: h.name,
    startDate: h.date,
    endDate: h.date,
  }
}

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function daysUntil(iso: string): number {
  const d = new Date(iso + 'T00:00:00')
  const t = new Date(todayIso() + 'T00:00:00')
  return Math.round((d.getTime() - t.getTime()) / 86_400_000)
}

function countdownLabel(days: number): string {
  if (days <= 0) return 'today'
  if (days === 1) return 'tomorrow'
  if (days < 7) return `in ${days} days`
  if (days < 14) return 'in 1 week'
  if (days < 30) return `in ${Math.round(days / 7)} weeks`
  if (days < 60) return 'in 1 month'
  return `in ${Math.round(days / 30)} months`
}

function formatDateRange(startIso: string, endIso: string): string {
  const s = new Date(startIso + 'T00:00:00')
  const e = new Date(endIso + 'T00:00:00')
  if (startIso === endIso) return format(s, 'EEE d MMM')
  if (s.getMonth() === e.getMonth()) {
    return `${format(s, 'd')}–${format(e, 'd MMM')}`
  }
  return `${format(s, 'd MMM')} – ${format(e, 'd MMM')}`
}
